import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { moduleService } from "../../services/moduleService";
import { mappingService } from "../../services/mappingService";
import { ImportModal } from "../user/ImportModal";
import { User } from "../../types/user";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { useUser } from '../../contexts/UserContext';
import ImportResultsModal from '../user/ImportResultsModal';
import { useNavigate } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import { Input } from "../ui/input";
import { ExportExcel } from '../ui/ExportExcel';

interface MappingComponentProps {
  moduleId: string;
}

export const MappingComponent: React.FC<MappingComponentProps> = ({ moduleId }) => {
  const [module, setModule] = useState<any>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importResults, setImportResults] = useState<any[] | null>(null);
  const { currentUser } = useUser();
  const navigate = useNavigate();
  const [filterUsername, setFilterUsername] = useState("");
  const [filterName, setFilterName] = useState("");

  useEffect(() => {
    if (moduleId) {
      fetchModuleInfo();
      fetchMappedUsers();
    }
    // eslint-disable-next-line
  }, [moduleId]);

  const fetchModuleInfo = async () => {
    const data = await moduleService.getModuleById(moduleId);
    setModule(data);
  };

  const fetchMappedUsers = async () => {
    setLoading(true);
    try {
      const distributorCode = currentUser?.distributorCode;
      if (!distributorCode) {
        throw new Error('Distributor code is required to fetch mapped users.');
      }
      const data = await mappingService.getMappedUsers(moduleId, distributorCode);
      setUsers(data);
    } catch (error) {
      // Optionally show a toast or log the error
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportSuccess = (response: any) => {
    setImportOpen(false);
    setImportResults(response.results || []);
    fetchMappedUsers();
  };

  // Filtered users
  const filteredUsers = users.filter(user =>
    (filterUsername === '' || user.username.toLowerCase().includes(filterUsername.toLowerCase())) &&
    (filterName === '' || user.name.toLowerCase().includes(filterName.toLowerCase()))
  );

  return (
    <div className="container mx-auto p-6">
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
          <CardTitle>Module Basic Info</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {module ? (
            <div className="grid grid-cols-2 gap-x-8 gap-y-2">
              <div><b>Name:</b> {module.name}</div>
              <div><b>Description:</b> {module.description}</div>
              <div><b>Seller Type:</b> {module.sellerType}</div>
              <div><b>Start Date:</b> {module.startDate ? new Date(module.startDate).toLocaleDateString() : '-'}</div>
              <div><b>End Date:</b> {module.endDate ? new Date(module.endDate).toLocaleDateString() : '-'}</div>
            </div>
          ) : (
            <div>Loading module info...</div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Mapped Users</CardTitle>
          <div className="flex gap-2">
            <Button onClick={() => setImportOpen(true)}>Import Users</Button>
            <ExportExcel
              data={filteredUsers}
              columns={[
                { label: 'Username', key: 'username' },
                { label: 'Name', key: 'name' },
                { label: 'Role', key: 'role' },
                { label: 'Seller Type', key: 'sellerType' },
              ]}
              filename="mapped-users.xlsx"
            />
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters and Count */}
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium mb-1">Username</label>
              <Input
                value={filterUsername}
                onChange={e => setFilterUsername(e.target.value)}
                placeholder="Filter by username"
                className="w-40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Name</label>
              <Input
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
                placeholder="Filter by name"
                className="w-40"
              />
            </div>
            <Button
              variant="outline"
              className="h-9"
              onClick={() => {
                setFilterUsername('');
                setFilterName('');
              }}
              disabled={
                !filterUsername && !filterName
              }
            >
              <X className="mr-2 h-4 w-4" />
              Clear Filters
            </Button>
            <div className="ml-auto text-sm font-medium">
              Count: {filteredUsers.length}
            </div>
          </div>
          {loading ? (
            <div>Loading users...</div>
          ) : users.length === 0 ? (
            <div>No users mapped to this module.</div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Seller Type</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                        No records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell className="capitalize">{user.role}</TableCell>
                    <TableCell>{user.sellerType}</TableCell>
                  </TableRow>
                    ))
                  )}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>
      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={handleImportSuccess}
        onImport={(data) => {
          const distributor = currentUser?.distributorCode || '';
          const user = currentUser?.username || '';
          const dataWithDistributor = data.map((row: any) => ({
            Username: row.Username !== undefined && row.Username !== null && row.Username !== '' ? String(row.Username) : '', // seller username from excel file
            Distributor: distributor !== undefined && distributor !== null && distributor !== '' ? String(distributor) : '', // distributor_admin distributor code
            User: user !== undefined && user !== null && user !== '' ? String(user) : '', // distributor_admin username
          }));
          return mappingService.importMappedUsers(moduleId, dataWithDistributor);
        }}
        title="Import Mapped Users"
        sampleTemplateUrl="/import-sample-excel/ImportModuleUserMappingSample.xlsx"
      />
      {importResults && (
        <ImportResultsModal
          results={importResults}
          onClose={() => setImportResults(null)}
      />
      )}
    </div>
  );
}; 